/**
 * Settings → Feature preferences tab.
 *
 * Configurable devcontainer features grouped by category. Moved verbatim from
 * the retired FeaturePreferencesPage; only the page chrome was removed.
 */

import type { ReactElement } from 'react';
import { useFeaturePreferences, FeatureConfigCard } from '@features/devcontainer';
import type { ConfigurableFeature } from '@features/devcontainer';

function groupByCategory(
  features: ConfigurableFeature[],
): Record<string, ConfigurableFeature[]> {
  const groups: Record<string, ConfigurableFeature[]> = {};
  for (const feature of features) {
    const category = feature.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(feature);
  }
  return groups;
}

const CATEGORY_LABELS: Record<string, string> = {
  languages: 'Languages',
  cloud: 'Cloud',
  'ai-tool': 'AI Tools',
};

function getCategoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

export function FeaturePreferencesTab(): ReactElement {
  const { data: features, isLoading, error } = useFeaturePreferences();

  if (isLoading) {
    return <div className="settings-loading">Loading features...</div>;
  }

  if (error) {
    return (
      <div className="settings-error">
        Failed to load features. Please refresh the page.
      </div>
    );
  }

  const featureList = features ?? [];
  const grouped = groupByCategory(featureList);
  const categoryKeys = Object.keys(grouped);

  if (featureList.length === 0) {
    return (
      <>
        <p className="feature-preferences-page__subtitle">
          Configure your personal settings for development features
        </p>
        <div className="feature-preferences-page__empty">
          No configurable features available.
        </div>
      </>
    );
  }

  return (
    <>
      <p className="feature-preferences-page__subtitle">
        Configure your personal settings for development features
      </p>
      {categoryKeys.map((category) => (
        <section key={category} className="feature-preferences-page__category">
          <h2 className="feature-preferences-page__category-title">
            {getCategoryLabel(category)}
          </h2>
          <div className="feature-preferences-page__cards">
            {grouped[category]?.map((feature) => (
              <FeatureConfigCard key={feature.feature_id} feature={feature} />
            ))}
          </div>
        </section>
      ))}
    </>
  );
}
